import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import useAuthStore from '@/stores/useAuthStore'
import authSdk from '@/utils/sdk'
import { ChatMessage } from '../types/response'

interface SocketConfig {
    socketUrl: string
    path: string
}

interface UseLivechatSocketOptions {
    roomId?: string
    onNewMessage?: (message: ChatMessage) => void
    onUserJoined?: (data: {
        userId: string
        userName: string
        userType: string
    }) => void
    onUserLeft?: (data: { userId: string; userName: string }) => void
    onTyping?: (data: { userId: string; userName: string }) => void
    onStopTyping?: (data: { userId: string }) => void
    onChatDone?: (data: { roomId: string }) => void
}

interface SendMessagePayload {
    roomId: string
    content: string
    type?: 'text' | 'image' | 'audio' | 'video' | 'file' | 'sticker'
    fileUrl?: string
    fileSize?: number
    fileName?: string
}

const config: SocketConfig = {
    socketUrl: import.meta.env.APP_LIVECHAT_SOCKET_URL,
    path: '/event',
}

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2000

export const useLivechatSocket = (options: UseLivechatSocketOptions = {}) => {
    const { employee } = useAuthStore()
    const socketRef = useRef<Socket | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const [isConnecting, setIsConnecting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const retryCountRef = useRef(0)
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const {
        roomId,
        onNewMessage,
        onUserJoined,
        onUserLeft,
        onTyping,
        onStopTyping,
        onChatDone,
    } = options

    // Initialize socket connection
    const connect = useCallback(() => {
        if (!employee || socketRef.current?.connected) return

        setIsConnecting(true)
        setError(null)

        // Get access token from auth SDK (same token DataCentral uses for API calls)
        const accessToken = authSdk.getAccessToken()

        if (!accessToken) {
            console.error('[Livechat Socket] No access token found')
            setError('No access token')
            setIsConnecting(false)
            return
        }

        const socket = io(config.socketUrl, {
            path: config.path,
            auth: {
                authorization: accessToken, // Backend expects auth.authorization
            },
            reconnection: false,
            timeout: 10000,
            transports: ['websocket', 'polling'],
        })

        socketRef.current = socket

        // Connection established
        socket.on('connect', () => {
            console.log('[Livechat Socket] Connected')
            retryCountRef.current = 0
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current)
            }
        })

        // VChat ready - user authenticated
        socket.on('connected', (data) => {
            console.log('[Livechat Socket] Authenticated:', data)
            setIsConnected(true)
            setIsConnecting(false)
        })

        // New message received
        socket.on('new-message', (data: ChatMessage) => {
            console.log('[Livechat Socket] New message:', data)
            onNewMessage?.(data)
        })

        // User joined room
        socket.on('user-joined', (data) => {
            console.log('[Livechat Socket] User joined:', data)
            onUserJoined?.(data)
        })

        // User left room
        socket.on('left-room', (data) => {
            console.log('[Livechat Socket] User left:', data)
            onUserLeft?.(data)
        })

        // Typing indicator
        socket.on('typing', (data) => {
            onTyping?.(data)
        })

        socket.on('stop-typing', (data) => {
            onStopTyping?.(data)
        })

        // Chat marked as done
        socket.on('chat-done', (data) => {
            console.log('[Livechat Socket] Chat done:', data)
            onChatDone?.(data)
        })

        // Connection error
        socket.on('connect_error', (err) => {
            console.error('[Livechat Socket] Connection error:', err.message)
            console.error('[Livechat Socket] Error details:', err)
            setError(err.message)
            setIsConnecting(false)
            handleRetry()
        })

        // Disconnected
        socket.on('disconnect', (reason) => {
            console.log('[Livechat Socket] Disconnected:', reason)
            setIsConnected(false)

            // Retry for unexpected disconnections
            if (
                reason === 'io server disconnect' ||
                reason === 'transport close'
            ) {
                handleRetry()
            }
        })

        return socket
    }, [
        employee,
        onNewMessage,
        onUserJoined,
        onUserLeft,
        onTyping,
        onStopTyping,
        onChatDone,
    ])

    // Retry connection
    const handleRetry = useCallback(() => {
        if (retryCountRef.current >= MAX_RETRIES) {
            setError('Failed to connect after multiple attempts')
            return
        }

        retryCountRef.current += 1
        const delay = RETRY_DELAY_MS * retryCountRef.current

        console.log(
            `[Livechat Socket] Retrying in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`
        )

        retryTimeoutRef.current = setTimeout(() => {
            connect()
        }, delay)
    }, [connect])

    // Disconnect socket
    const disconnect = useCallback(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current)
        }
        if (socketRef.current) {
            socketRef.current.disconnect()
            socketRef.current = null
        }
        setIsConnected(false)
        setIsConnecting(false)
    }, [])

    // Join a room
    const joinRoom = useCallback(
        (
            targetRoomId: string
        ): Promise<{ success: boolean; room_id: string }> => {
            return new Promise((resolve, reject) => {
                if (!socketRef.current?.connected) {
                    reject(new Error('Socket not connected'))
                    return
                }

                socketRef.current.emit(
                    'join-room',
                    { roomId: targetRoomId },
                    (response: any) => {
                        if (response?.success) {
                            resolve(response)
                        } else {
                            reject(
                                new Error(
                                    response?.message || 'Failed to join room'
                                )
                            )
                        }
                    }
                )
            })
        },
        []
    )

    // Leave a room
    const leaveRoom = useCallback(
        (targetRoomId: string): Promise<{ success: boolean }> => {
            return new Promise((resolve, reject) => {
                if (!socketRef.current?.connected) {
                    reject(new Error('Socket not connected'))
                    return
                }

                socketRef.current.emit(
                    'left-room',
                    { roomId: targetRoomId },
                    (response: any) => {
                        if (response?.success) {
                            resolve(response)
                        } else {
                            reject(
                                new Error(
                                    response?.message || 'Failed to leave room'
                                )
                            )
                        }
                    }
                )
            })
        },
        []
    )

    // Send message
    const sendMessage = useCallback(
        (
            payload: SendMessagePayload
        ): Promise<{ success: boolean; data: ChatMessage }> => {
            return new Promise((resolve, reject) => {
                if (!socketRef.current?.connected) {
                    reject(new Error('Socket not connected'))
                    return
                }

                const messagePayload = {
                    roomId: payload.roomId,
                    content: payload.content,
                    metadata: {
                        type: payload.type || 'text',
                        fileUrl: payload.fileUrl,
                        fileSize: payload.fileSize,
                        fileName: payload.fileName,
                    },
                }

                socketRef.current.emit(
                    'send-message',
                    messagePayload,
                    (response: any) => {
                        if (response?.success) {
                            resolve(response)
                        } else {
                            reject(
                                new Error(
                                    response?.message ||
                                        'Failed to send message'
                                )
                            )
                        }
                    }
                )
            })
        },
        []
    )

    // Send typing indicator
    const sendTyping = useCallback((targetRoomId: string) => {
        if (!socketRef.current?.connected) return
        socketRef.current.emit('typing', { roomId: targetRoomId })
    }, [])

    // Send stop typing indicator
    const sendStopTyping = useCallback((targetRoomId: string) => {
        if (!socketRef.current?.connected) return
        socketRef.current.emit('stop-typing', { roomId: targetRoomId })
    }, [])

    // Mark chat as done
    const markChatDone = useCallback(
        (targetRoomId: string): Promise<{ success: boolean }> => {
            return new Promise((resolve, reject) => {
                if (!socketRef.current?.connected) {
                    reject(new Error('Socket not connected'))
                    return
                }

                socketRef.current.emit(
                    'done-chat',
                    { roomId: targetRoomId },
                    (response: any) => {
                        if (response?.success) {
                            resolve(response)
                        } else {
                            reject(
                                new Error(
                                    response?.message ||
                                        'Failed to mark chat as done'
                                )
                            )
                        }
                    }
                )
            })
        },
        []
    )

    // Block user
    const blockUser = useCallback(
        (
            userId: string,
            targetRoomId: string
        ): Promise<{ success: boolean }> => {
            return new Promise((resolve, reject) => {
                if (!socketRef.current?.connected) {
                    reject(new Error('Socket not connected'))
                    return
                }

                socketRef.current.emit(
                    'block-sender',
                    { userId, roomId: targetRoomId },
                    (response: any) => {
                        if (response?.success) {
                            resolve(response)
                        } else {
                            reject(
                                new Error(
                                    response?.message || 'Failed to block user'
                                )
                            )
                        }
                    }
                )
            })
        },
        []
    )

    // Auto-connect on mount
    useEffect(() => {
        if (employee) {
            connect()
        }

        return () => {
            disconnect()
        }
    }, [employee, connect, disconnect])

    // Auto-join room when roomId changes
    useEffect(() => {
        if (isConnected && roomId) {
            joinRoom(roomId).catch((err) => {
                console.error('[Livechat Socket] Failed to join room:', err)
            })
        }
    }, [isConnected, roomId, joinRoom])

    return {
        isConnected,
        isConnecting,
        error,
        connect,
        disconnect,
        joinRoom,
        leaveRoom,
        sendMessage,
        sendTyping,
        sendStopTyping,
        markChatDone,
        blockUser,
    }
}

export default useLivechatSocket
